import React, { useEffect, useMemo } from "react";
import { appetizeClientAtom, appetizeSessionStore } from "@/atoms";
import { useAtomValue } from "jotai";
import { PlayIcon, StopIcon } from "@heroicons/react/24/outline";
import BorderColorIcon from "@mui/icons-material/BorderColor";
import { useLocation } from "react-router";
import {
  type BuildData,
  type CompanyData,
  type RunExecutionData,
  type TestSuiteData,
} from "@/utils/types";
import { pushMediaToAppetize } from "@/utils/media-helpers";
import {
  getErrorMessage,
  getPromptStepsText,
  getSessionMutationAtom,
  shouldUseAppetizeClone,
  trackExceptionOnFirestore,
  trackSentryException,
} from "@/utils/helpers";
import useExecuteSteps from "../routes/test-run/use-execute-steps";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import Timeout from "await-timeout";
import { CircularProgress } from "@mui/material";
import { useStore } from "@tanstack/react-store";

const kRetryTimeout = 60000;
const kAppetizeStartSessionTimeout = 180000;

export default function TestRunGptEditor({
  runs,
  buildData,
  companyData,
  testSuite,
}: {
  runs: RunExecutionData[];
  companyData: CompanyData;
  buildData: BuildData;
  testSuite: TestSuiteData;
}) {
  const location = useLocation();

  const shouldAutoRun = useMemo<boolean>(() => {
    const urlSearchParams = new URLSearchParams(location.search);
    const autoRunParam = urlSearchParams.get("autorun");

    return autoRunParam === "true";
  }, [location]);

  const session = useStore(appetizeSessionStore, (state) => state);
  const appetizeClient = useAtomValue(appetizeClientAtom);

  const {
    executionState,
    startExecution,
    stopExecution,
    concludedParentRunMutation,
  } = useExecuteSteps({
    testSuite,
    buildData,
    companyData,
  });

  const { mutateAsync, reset } = useAtomValue(
    getSessionMutationAtom(testSuite?.deviceProviderSettings)
  );
  const queryClient = useQueryClient();

  const autoRunQueryData = useQuery({
    queryKey: ["autorun"],
    queryFn: async () => {
      const date = new Date();
      // The background runner polls for this element to know the autorun
      // has actually started. The sibling div is gated on the same render
      // conditions as this component, but assert-and-bail on null here so
      // a future render-order change can't silently break heartbeats.
      const heartbeatElement = document.getElementById("heartbeat");
      if (heartbeatElement == null) {
        throw new Error(
          "heartbeat element missing at autorun start — likely a render-order race"
        );
      }
      heartbeatElement.innerText = `STARTING_SESSION_FROM_AUTORUN_QUERY,${date.getTime()}`;

      const isStealthMode = testSuite.stealthMode;
      let companySettings = companyData.settings;
      if (
        testSuite.deviceProviderSettings?.provider === "gptdriver" &&
        testSuite.deviceProviderSettings?.usePhysicalDevice &&
        (!companySettings.enableAppetizeCloneCloud ||
          companySettings.fallbackInfrastructure.enabled)
      ) {
        companySettings = {
          ...companySettings,
          enableAppetizeCloneCloud: true,
          fallbackInfrastructure: {
            ...companySettings.fallbackInfrastructure,
            enabled: false,
          },
        };
        queryClient.setQueryData(
          ["getCompanyData", buildData.organisationId],
          (prevData: CompanyData) => ({
            ...prevData,
            settings: companySettings,
          })
        );
      }

      let usingAppetizeClone = shouldUseAppetizeClone({
        settingsData: companySettings,
        buildData,
        fromEditor: false,
      });
      if (
        isStealthMode &&
        !companyData.settings.ignoreStealthForPlatform &&
        !usingAppetizeClone
      ) {
        queryClient.setQueryData(
          ["getCompanyData", buildData.organisationId],
          (oldData: CompanyData) => ({
            ...oldData,
            settings: {
              ...oldData.settings,
              enableAppetizeCloneCloud: true,
            },
          })
        );
        usingAppetizeClone = true;
      }

      if (usingAppetizeClone) {
        const parentRun = runs.at(-1)!;

        await mutateAsync({
          buildId: buildData.id,
          bundleId: buildData.bundle!,
          storagePath: buildData.storage_path,
          device: parentRun.device,
          os: parentRun.os,
          platform: parentRun.platform,
          language: parentRun.locale?.split("_")?.at(0),
          countryCode: parentRun.locale?.split("_")?.at(1),
          proxy:
            companyData.settings.proxyAppetizeClone ??
            companyData.settings.proxy,
          grantPermissions: companyData.settings.autoGrantPermissions,
          onCleanup: () => {
            reset();
          },
          organisationId: companyData.organisationId,
          testRunId: parentRun.id,
          testSuite,
          testTitle: parentRun.runData?.title,
          launchParams: parentRun.settings.launchParams,
          mediaFiles: parentRun.settings?.mediaFiles,
          usePhysicalDevice:
            testSuite?.deviceProviderSettings?.usePhysicalDevice ?? false,
        });
      } else {
        if (appetizeClient.session == null) {
          await Timeout.wrap(
            appetizeClient.startSession(),
            kAppetizeStartSessionTimeout
          );
        }
        if (appetizeClient.session == null) {
          throw Error("Could not initialize emulator session");
        } else if (!appetizeClient.session.ready) {
          throw Error(
            `Could not initialize emulator session after ${
              kAppetizeStartSessionTimeout / 1000
            } seconds.`
          );
        }

        // Push media files to Appetize session before test execution
        const parentRun = runs.at(-1)!;
        const mediaFilesToPush = parentRun.settings?.mediaFiles ?? [];
        if (mediaFilesToPush.length > 0) {
          await pushMediaToAppetize(appetizeClient.session, mediaFilesToPush);
        }
      }

      const secondDate = new Date();
      heartbeatElement.innerText = `SESSION_STARTED_FROM_AUTORUN_QUERY,${secondDate.getTime()}`;

      return { runs, enableRetry: true };
    },
    retry: 5,
    retryDelay: kRetryTimeout,
    enabled:
      (companyData.settings.enableAppetizeCloneCloud ||
        testSuite.deviceProviderSettings?.provider === "gptdriver" ||
        (!companyData.settings.enableAppetizeCloneCloud &&
          appetizeClient != null)) &&
      shouldAutoRun,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
  });

  useEffect(() => {
    if (autoRunQueryData.data != null) {
      startExecution(autoRunQueryData.data);
    }
  }, [autoRunQueryData.data]);

  useEffect(() => {
    if (autoRunQueryData.isError) {
      trackSentryException(autoRunQueryData.error);
      trackExceptionOnFirestore({
        error: getErrorMessage(autoRunQueryData.error),
        source: "appetize",
      });
      if (companyData.settings.enableAppetizeCloneCloud) {
        const supportsFallback =
          companyData.settings.fallbackInfrastructure.enabled &&
          (companyData.settings.fallbackInfrastructure.platform == null ||
            companyData.settings.fallbackInfrastructure.platform.toLowerCase() ===
              buildData.platform.toLowerCase());

        const isValidErrorCodeForFallback = [500, 404, 429].includes(
          autoRunQueryData.error?.response?.status ?? -1
        );

        if (supportsFallback && isValidErrorCodeForFallback) {
          queryClient.setQueryData(
            ["getCompanyData", buildData.organisationId],
            (oldData: CompanyData) => ({
              ...oldData,
              settings: {
                ...oldData.settings,
                enableAppetizeCloneCloud: false,
                ignoreStealthForPlatform: true,
              },
            })
          );
          queryClient.removeQueries({ queryKey: ["autorun"] });
        } else {
          concludedParentRunMutation.mutate({
            status: "blocked",
            statusMessage: getErrorMessage(
              isValidErrorCodeForFallback
                ? autoRunQueryData.error.message
                : `unexpected error ${autoRunQueryData.error}`
            ),
            run: runs[runs.length - 1],
          });
        }
      } else {
        concludedParentRunMutation.mutate({
          status: "blocked",
          statusMessage: getErrorMessage(autoRunQueryData.error),
          run: runs[runs.length - 1],
        });
      }
    }
  }, [
    autoRunQueryData.error,
    companyData.settings.enableAppetizeCloneCloud,
    buildData,
  ]);

  return (
    <>
      <div className={"flex gap-2 items-center mb-8"}>
        <h1 className="text-white text-lg ">Test Title</h1>
        <BorderColorIcon fontSize={"small"} className={"text-[#7C7C7C]"} />
      </div>
      <h3 className="text-[#ccc] text-sm mb-4">Test Prompt</h3>
      <textarea
        className="w-full p-4 bg-black text-[#eee] rounded-[16px] h-[120px]"
        value={runs
          .map(
            (run, index) =>
              `Test #${index + 1}\n${getPromptStepsText(run.promptSteps)}`
          )
          .join("\n\n")}
        readOnly={true}
      ></textarea>
      <div className="flex gap-4 pt-4">
        <div className="mr-4">
          {!executionState.isRunning && (
            <button
              className="text-white font-light py-2 px-4 enabled:hover:bg-[#232323] disabled:bg-gray-800"
              style={{ border: "1px solid #656565" }}
              onClick={() => {
                startExecution({ runs, enableRetry: true });
              }}
              disabled={session.getData() == null}
            >
              <PlayIcon strokeWidth={2} className="h-5 w-5 inline" /> Execute
            </button>
          )}
          {executionState.isRunning && (
            <div className={"flex gap-4"}>
              <button
                className="text-[#1C1C1E] font-light py-2 px-4 bg-[#0DFF25]"
                style={{ border: "1px solid #656565" }}
                onClick={stopExecution}
              >
                <StopIcon strokeWidth={2} className="h-5 w-5 inline" /> Stop
              </button>
              <CircularProgress />
            </div>
          )}
        </div>
        <div className="inline text-[#ccc] text-sm">
          {executionState.isRunning ? "Executing" : ""}
        </div>
      </div>

      {executionState.result && (
        <div className="mt-8">
          <div
            className={`${
              executionState.result === "Success"
                ? `bg-green-800`
                : `bg-red-800`
            } p-2 text-white rounded-lg rounded-b-none`}
          >
            {executionState.result}
          </div>
          <div
            className={`${
              executionState.result === "Success"
                ? `bg-green-200`
                : `bg-red-200`
            } p-2 rounded-lg rounded-t-none`}
          >
            {executionState.resultContent}
          </div>
        </div>
      )}

      <h3 className="text-[#7C7C7C] text-[18px] mt-8 mb-4">Execution Log</h3>

      <div className="flex flex-col w-full flex-grow-1 rounded-t-[16px] overflow-auto">
        <div className="grid grid-cols-2 text-left text-[#5C5C5C] bg-[#1E1E1E] ">
          <div className="font-semibold mx-auto p-4">Screen</div>
          <div className="font-semibold mx-auto p-4">Actions</div>
        </div>
        {executionState.runs.length > 0 && (
          <div
            className={
              "flex flex-col gap-1 flex-grow-1 rounded-b-[16px] bg-black"
            }
          >
            {executionState.runs.map((run, runIndex) => (
              <>
                {run.executionSteps.map((step, stepIndex) => (
                  <div
                    className="grid grid-cols-2 text-left text-[#232323]  divide-x divide-[#656565]"
                    key={`${runIndex}-${stepIndex}`}
                  >
                    <div className={"p-5"}>
                      <img src={step.screenshot} />
                    </div>
                    <div className={"p-5 text-white"}>
                      {step.gptCommands.map((gptStep, gptCommandIndex) => (
                        <div key={gptCommandIndex}>{gptStep}</div>
                      ))}
                    </div>
                  </div>
                ))}
              </>
            ))}
          </div>
        )}
      </div>
    </>
  );
}
